import { useMemo, useState } from 'react';
import { Link } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { api, keys, useMeta } from '../../api.ts';
import { roundBand } from '../../../shared/format.ts';
import { ErrorBox, Help, Loading } from '../../components/ui.tsx';
import { useUserContext } from './UserLayout.tsx';

type Filter = 'all' | 'missing' | 'worked';

export function MatrixPage() {
  const { user } = useUserContext();
  const meta = useMeta();
  const [filter, setFilter] = useState<Filter>('all');
  const [showDeleted, setShowDeleted] = useState(false);
  const q = useQuery({
    queryKey: keys.matrix(user.id),
    queryFn: () => api.get<{ cells: Array<{ countryId: number; band: number; count: number }> }>(`/users/${encodeURIComponent(user.id)}/matrix`),
  });

  const grid = useMemo(() => {
    const m = new Map<number, Map<number, number>>();
    for (const c of q.data?.cells ?? []) {
      const row = m.get(c.countryId) ?? new Map<number, number>();
      row.set(roundBand(c.band), c.count);
      m.set(c.countryId, row);
    }
    return m;
  }, [q.data]);

  if (meta.isLoading || q.isLoading) return <Loading />;
  if (q.error) return <ErrorBox error={q.error} />;

  const bands = meta.data?.bands ?? [];
  const countries = [...(meta.data?.countries ?? [])]
    .filter((c) => showDeleted || !c.deleted)
    .sort((a, b) => Number(a.deleted) - Number(b.deleted) || a.name.localeCompare(b.name, 'fi'))
    .filter((c) => (filter === 'missing' ? !grid.has(c.id) : filter === 'worked' ? grid.has(c.id) : true));
  const current = new Set((meta.data?.countries ?? []).filter((c) => !c.deleted).map((c) => c.id));
  const perBand = bands.map((b) => [...grid.entries()].filter(([cid, row]) => row.has(b.band) && current.has(cid)).length);
  const worked = [...grid.keys()].filter((id) => current.has(id)).length;
  const qsoUrl = (params: string) => `/users/${encodeURIComponent(user.id)}/qsos?${params}`;

  return (
    <div className="stack">
      <div className="row">
        <div className="stat" style={{ minWidth: 180 }}>
          <div className="label">Workitut nykymaat</div>
          <div className="value">{worked}</div>
        </div>
        <span className="spacer" />
        <div className="radio-group">
          {(
            [
              ['all', 'Kaikki maat'],
              ['missing', 'Vain puuttuvat'],
              ['worked', 'Vain workitut'],
            ] as const
          ).map(([v, l]) => (
            <label key={v} className="check">
              <input type="radio" name="filter" checked={filter === v} onChange={() => setFilter(v)} /> {l}
            </label>
          ))}
          <label className="check">
            <input type="checkbox" checked={showDeleted} onChange={(e) => setShowDeleted(e.target.checked)} /> Näytä poistuneet
          </label>
        </div>
        <Help name="Matriisi" />
      </div>
      <div className="table-wrap table-scroll">
        <table className="table matrix">
          <thead>
            <tr>
              <th style={{ textAlign: 'left' }}>Maa</th>
              <th>Prefiksi</th>
              {bands.map((b) => (
                <th key={b.band}>{b.label}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            <tr>
              <td className="name muted">Maita bandilla</td>
              <td />
              {perBand.map((n, i) => (
                <td key={bands[i]!.band} className="mono muted">
                  {n || ''}
                </td>
              ))}
            </tr>
            {countries.map((c) => {
              const row = grid.get(c.id);
              return (
                <tr key={c.id}>
                  <td className="name">
                    <Link to={qsoUrl(`countries=${c.id}`)} title={c.name}>
                      {c.name}
                    </Link>
                    {c.deleted && <span className="faint"> (poistunut)</span>}
                  </td>
                  <td className="mono muted">{c.code}</td>
                  {bands.map((b) => {
                    const n = row?.get(b.band);
                    return (
                      <td key={b.band} className={n ? 'cell-hit' : 'cell-missing'}>
                        {n ? (
                          <Link className="mono" to={qsoUrl(`countries=${c.id}&bands=${b.band}`)}>
                            {n}
                          </Link>
                        ) : null}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
